import { useState, useEffect } from 'react';   // ← was missing useEffect
import AdminLayout from '../../../components/feature/AdminLayout';
import Card from '../../../components/base/Card';
import JeansSalesTab from './JeansSalesTab';
import ShirtsSalesTab from './ShirtsSalesTab';
import OverviewTab from './OverviewTab';
import { getData } from '../../../services/FetchNodeServices';

const emptySalesData = {
  jeans: { total: 0, pieces: 0, growth: 0, orders: 0, avgOrder: 0, dailyData: [] },
  shirts: { total: 0, pieces: 0, growth: 0, orders: 0, avgOrder: 0, dailyData: [] },
};

export default function SalesReports() {
  const [activeTab, setActiveTab] = useState('overview');
  const [dateRange, setDateRange] = useState('thisMonth');
  const [selectedPeriod, setSelectedPeriod] = useState('Monthly');
  const [salesDatas, setSalesData] = useState(emptySalesData);
  const [topProducts, setTopProducts] = useState([]);
  const [loadingSales, setLoadingSales] = useState(false);
  const [loadingTop, setLoadingTop] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // ── Fetch sales trend data ──────────────────────────────────────────────────
  useEffect(() => {
    const fetchData = async () => {
      setLoadingSales(true);
      try {
        const res = await getData(
          `api/salesAndReports/get-SalesData?range=${startDate || endDate ? 'custom' : dateRange}&startDate=${startDate}&endDate=${endDate}`
        );
        if (res?.success) setSalesData(res.data || emptySalesData);
      } catch (err) {
        console.error("Failed to fetch sales data:", err);
      } finally {
        setLoadingSales(false);
      }
    };
    fetchData();
  }, [dateRange, selectedPeriod, startDate, endDate]);

  // ── Fetch top products ──────────────────────────────────────────────────────
  useEffect(() => {
    const fetchTopProductData = async () => {
      setLoadingTop(true);
      try {
        const res = await getData(
          `api/salesAndReports/get-top-products?range=${startDate || endDate ? 'custom' : dateRange}&startDate=${startDate}&endDate=${endDate}`
        );
        if (res?.success) setTopProducts(res.data || []);
      } catch (err) {
        console.error("Failed to fetch top products:", err);
      } finally {
        setLoadingTop(false);
      }
    };
    fetchTopProductData();
  }, [dateRange, startDate, endDate]);

  // ── Sales trend SVG bar chart ───────────────────────────────────────────────
  const renderChart = () => {
    const jeansSales = salesDatas?.jeans?.dailyData?.map(d => d.sales) || [0];
    const shirtsSales = salesDatas?.shirts?.dailyData?.map(d => d.sales) || [0];
    const maxValue = Math.max(...jeansSales, ...shirtsSales, 1);

    const allDates = Array.from(
      new Set([
        ...(salesDatas?.jeans?.dailyData?.map(d => d.date) || []),
        ...(salesDatas?.shirts?.dailyData?.map(d => d.date) || []),
      ])
    ).sort((a, b) => new Date(a) - new Date(b));

    const getSalesForDate = (categoryData = [], date) =>
      categoryData.find(d => d.date === date)?.sales || 0;

    const getPcsForDate = (categoryData = [], date) =>
      categoryData.find(d => d.date === date)?.pieces || 0;

    if (allDates.length === 0) {
      return (
        <div className="h-64 flex items-center justify-center text-gray-400 text-sm">
          No data for selected period
        </div>
      );
    }

    const chartWidth = 600;
    const chartHeight = 160;
    const paddingTop = 10;
    const paddingLeft = 50;
    const paddingRight = 20;
    const usableWidth = chartWidth - paddingLeft - paddingRight;

    const yFor = (value) => paddingTop + chartHeight - (value / maxValue) * chartHeight;
    const hFor = (value) => (value / maxValue) * chartHeight;

    const groupWidth = usableWidth / allDates.length;
    const barWidth = Math.max(4, Math.min(16, groupWidth / 2 - 4));
    const groupPadding = (groupWidth - barWidth * 2) / 2;
    const xForGroup = (i) => paddingLeft + i * groupWidth;

    return (
      <div className="bg-gray-50 rounded-lg p-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-medium text-sm text-gray-700">Sales Trend ({selectedPeriod})</h3>
          <div className="flex space-x-4 text-xs">
            <div className="flex items-center gap-1"><div className="w-3 h-3 rounded-sm bg-blue-500" /><span>Jeans</span></div>
            <div className="flex items-center gap-1"><div className="w-3 h-3 rounded-sm bg-green-500" /><span>Shirts</span></div>
          </div>
        </div>

        <svg viewBox={`0 0 ${chartWidth} ${chartHeight + paddingTop + 30}`} className="w-full" style={{ height: 220 }}>
          {/* Grid lines */}
          {[0, 0.25, 0.5, 0.75, 1].map((pct, i) => (
            <g key={i}>
              <line x1={paddingLeft} y1={yFor(maxValue * pct)} x2={chartWidth - paddingRight} y2={yFor(maxValue * pct)} stroke="#E5E7EB" strokeWidth="1" />
              <text x={paddingLeft - 4} y={yFor(maxValue * pct) + 4} textAnchor="end" fontSize="9" fill="#9CA3AF">
                ₹{Math.round(maxValue * pct / 1000)}k
              </text>
            </g>
          ))}

          {/* Bars */}
          {allDates.map((date, i) => {
            const jeansVal = getSalesForDate(salesDatas.jeans.dailyData, date);
            const shirtsVal = getSalesForDate(salesDatas.shirts.dailyData, date);
            const gx = xForGroup(i);
            return (
              <g key={i}>
                {/* Jeans bar */}
                <rect x={gx + groupPadding} y={yFor(jeansVal)} width={barWidth} height={hFor(jeansVal)} fill="#3B82F6" rx="2" opacity="0.85">
                  <title>Jeans {date}: ₹{jeansVal.toLocaleString()} | {getPcsForDate(salesDatas.jeans.dailyData, date)} Pcs</title>
                </rect>
                {/* Shirts bar */}
                <rect x={gx + groupPadding + barWidth + 2} y={yFor(shirtsVal)} width={barWidth} height={hFor(shirtsVal)} fill="#10B981" rx="2" opacity="0.85">
                  <title>Shirts {date}: ₹{shirtsVal.toLocaleString()} | {getPcsForDate(salesDatas.shirts.dailyData, date)} Pcs</title>
                </rect>
                {/* X-axis label */}
                <text x={gx + groupPadding + barWidth} y={paddingTop + chartHeight + 14} textAnchor="middle" fontSize="9" fill="#6B7280">
                  {new Date(date).getDate()}/{new Date(date).getMonth() + 1}
                </text>
              </g>
            );
          })}

          {/* Baseline */}
          <line x1={paddingLeft} y1={paddingTop + chartHeight} x2={chartWidth - paddingRight} y2={paddingTop + chartHeight} stroke="#D1D5DB" strokeWidth="1" />
        </svg>
      </div>
    );
  };

  // ── Pie / donut chart ───────────────────────────────────────────────────────
  const renderPieChart = () => {
    const jeansTotal = salesDatas?.jeans?.total || 0;
    const shirtsTotal = salesDatas?.shirts?.total || 0;
    const totalSales = jeansTotal + shirtsTotal;

    if (totalSales === 0) {
      return (
        <div className="h-64 flex items-center justify-center text-gray-400 text-sm">
          No data for selected period
        </div>
      );
    }

    const categoryDistribution = [
      { name: "Jeans", value: jeansTotal, pieces: salesDatas?.jeans?.pieces || 0, color: "#4F46E5" },
      { name: "Shirts", value: shirtsTotal, pieces: salesDatas?.shirts?.pieces || 0, color: "#22C55E" },
    ].map(c => ({ ...c, percentage: ((c.value / totalSales) * 100) }));

    const cx = 120, cy = 120, r = 80;
    let currentAngle = -Math.PI / 2;

    return (
      <div>
        <svg width="240" height="240" className="mx-auto">
          {categoryDistribution.map((cat, i) => {
            const angle = (cat.percentage / 100) * 2 * Math.PI;
            const x1 = cx + r * Math.cos(currentAngle);
            const y1 = cy + r * Math.sin(currentAngle);
            currentAngle += angle;
            const x2 = cx + r * Math.cos(currentAngle);
            const y2 = cy + r * Math.sin(currentAngle);
            const largeArc = angle > Math.PI ? 1 : 0;
            return (
              <path
                key={i}
                d={`M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2} Z`}
                fill={cat.color}
                className="hover:opacity-80 cursor-pointer transition-opacity"
              >
                <title>{cat.name}: ₹{cat.value.toLocaleString()} | {cat.pieces} Pcs ({cat.percentage.toFixed(1)}%)</title>
              </path>
            );
          })}
          <circle cx={cx} cy={cy} r="40" fill="white" />
          <text x={cx} y={cy - 8} textAnchor="middle" fontSize="10" fill="#374151">Total</text>
          <text x={cx} y={cy + 5} textAnchor="middle" fontSize="9" fill="#6B7280">₹{totalSales.toLocaleString()}</text>
          <text x={cx} y={cy + 17} textAnchor="middle" fontSize="9" fill="#6B7280">
            {(salesDatas?.jeans?.pieces || 0) + (salesDatas?.shirts?.pieces || 0)} Pcs
          </text>
        </svg>
        <div className="flex justify-center gap-6 mt-2">
          {categoryDistribution.map((cat, i) => (
            <div key={i} className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: cat.color }} />
              <span className="text-xs text-gray-600">{cat.name} ({cat.percentage.toFixed(1)}%)</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <AdminLayout>
      <div className="p-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Sales & Reports</h1>
            <p className="text-gray-500 mt-1 text-sm">Monitor your sales performance by category</p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {/* Conditional Date Inputs */}
            {dateRange === 'custom' && (
              <div className="flex items-center gap-2 animate-in fade-in duration-300">
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                />
                <span className="text-gray-400 text-sm">to</span>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>
            )}

            {/* Range Selector */}
            <div className="relative">
              <select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                className="px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white text-sm font-medium text-gray-700 cursor-pointer"
              >
                <option value="today">Today</option>
                <option value="thisWeek">This Week</option>
                <option value="thisMonth">This Month</option>
                <option value="thisYear">This Year</option>
                <option value="custom">Custom Range</option>
              </select>
              <i className="ri-calendar-line absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>
          </div>
        </div>
        {/* Tab Navigation */}
        <div className="flex space-x-1 mb-6">
          {[
            { id: 'overview', label: 'Overview', icon: 'ri-dashboard-line' },
            { id: 'jeans', label: 'Jeans Sales', icon: 'ri-shirt-line' },
            { id: 'shirts', label: 'Shirts Sales', icon: 'ri-t-shirt-line' },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === tab.id ? 'bg-blue-100 text-blue-700' : 'text-gray-500 hover:text-gray-700'
                }`}
            >
              <i className={`${tab.icon} mr-2`} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* ── Overview Tab ── */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Pass live data to OverviewTab */}
            <OverviewTab salesData={salesDatas} dateRange={dateRange} startDate={startDate} endDate={endDate} setDateRange={setDateRange} />

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold">Sales Trend</h3>
                  <div className="flex space-x-1">
                    {/* {['Daily', 'Weekly', 'Monthly', 'Yearly'].map(period => (
                      <button
                        key={period}
                        onClick={() => setSelectedPeriod(period)}
                        className={`px-3 py-1 text-xs rounded-full ${selectedPeriod === period
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                      >
                        {period}
                      </button>
                    ))} */}
                  </div>
                </div>
                {loadingSales
                  ? <div className="h-48 flex items-center justify-center text-gray-400 text-sm">Loading…</div>
                  : renderChart()
                }
              </Card>

              <Card className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold">Sales Distribution</h3>
                  <span className="text-sm text-gray-500">By Category</span>
                </div>
                {loadingSales
                  ? <div className="h-48 flex items-center justify-center text-gray-400 text-sm">Loading…</div>
                  : renderPieChart()
                }
              </Card>
            </div>

            {/* Top Products */}
            <Card>
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold">Top Performing Products</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      {['Product', 'Revenue & Pieces', 'Units Sold', 'Growth'].map(h => (
                        <th key={h} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {loadingTop ? (
                      <tr><td colSpan={4} className="px-6 py-8 text-center text-gray-400 text-sm">Loading…</td></tr>
                    ) : topProducts.length === 0 ? (
                      <tr><td colSpan={4} className="px-6 py-8 text-center text-gray-400 text-sm">No products found</td></tr>
                    ) : topProducts.map((product, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-3">
                            <div className={`h-8 w-8 rounded-full flex items-center justify-center text-white text-sm font-bold ${index === 0 ? 'bg-yellow-500' :
                              index === 1 ? 'bg-gray-400' :
                                index === 2 ? 'bg-orange-500' : 'bg-blue-500'
                              }`}>
                              {index + 1}
                            </div>
                            <span className="text-sm font-medium text-gray-900">{product.name}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          ₹{product.sales.toLocaleString()} &nbsp;|&nbsp; {product.pieces} Pcs
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                          {product.units} lots
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${product.growth >= 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}>
                            {product.growth >= 0 ? '+' : ''}{product.growth}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
        )}

        {/* ── Jeans Tab ── */}
        {activeTab === 'jeans' && (
          <JeansSalesTab setSalesData={setSalesData} salesData={salesDatas} />
        )}

        {/* ── Shirts Tab ── */}
        {activeTab === 'shirts' && (
          <ShirtsSalesTab setSalesData={setSalesData} salesData={salesDatas} />
        )}
      </div>
    </AdminLayout>
  );
}
